import {
  IAnalyticsService,
  AnalyticsEvent,
  AnalyticsConfig,
  AnalyticsError,
  AnalyticsSession,
  UserProperties,
  EventQueue,
  GameStartEvent,
  GameCompleteEvent,
  UserEngagementEvent,
  SettingsChangeEvent,
  ModeSwitchEvent,
  PerformanceIssueEvent,
  FirstGameCompleteEvent,
  StreakMilestoneEvent,
  ShareEvent,
  ModalOpenEvent
} from '../types/AnalyticsTypes';
import { analyticsConfig, canRunAnalytics } from '../config/analytics';

// Extend window interface for gtag
declare global {
  interface Window {
    gtag: (...args: any[]) => void;
    dataLayer: any[];
  }
}

export class AnalyticsService implements IAnalyticsService {
  private static instance: AnalyticsService;
  private isInitializedFlag: boolean = false;
  private config: AnalyticsConfig;
  private session: AnalyticsSession;
  private eventQueue: EventQueue;
  private userProperties: UserProperties;
  private flushTimeout: NodeJS.Timeout | null = null;

  private constructor(config: AnalyticsConfig = analyticsConfig) {
    this.config = config;
    this.session = this.createSession();
    this.eventQueue = {
      events: [],
      lastFlush: Date.now(),
      size: 0
    };
    this.userProperties = this.initializeUserProperties();
  }

  public static getInstance(config?: AnalyticsConfig): AnalyticsService {
    if (!AnalyticsService.instance) {
      AnalyticsService.instance = new AnalyticsService(config);
    }
    return AnalyticsService.instance;
  }

  // For testing purposes only
  public static resetInstance(): void {
    AnalyticsService.instance = undefined as any;
  }

  public async initialize(config?: AnalyticsConfig): Promise<void> {
    try {
      // Skip if already initialized
      if (this.isInitializedFlag) {
        return;
      }

      if (config) {
        this.config = { ...this.config, ...config };
      }

      // Check if analytics should run
      if (!canRunAnalytics()) {
        if (this.config.debugMode) {
          console.log('Analytics: Skipping initialization (disabled by configuration or user preference)');
        }
        return;
      }

      // Load Google Analytics
      await this.loadGoogleAnalytics();
      
      // Initialize session tracking
      this.initializeSessionTracking();
      
      // Set up performance monitoring integration
      this.setupPerformanceMonitoring();
      
      this.isInitializedFlag = true;
      
      if (this.config.debugMode) {
        console.log('Analytics: Successfully initialized', {
          measurementId: this.config.measurementId,
          session: this.session
        });
      }

      // Track initial page view
      this.trackPageView();

    } catch (error) {
      console.error('Analytics: Failed to initialize', error);
      throw new AnalyticsError(
        'Failed to initialize analytics service',
        'INIT_FAILED',
        error
      );
    }
  }

  public isInitialized(): boolean {
    return this.isInitializedFlag && canRunAnalytics();
  }

  public trackEvent<T extends AnalyticsEvent>(event: T): void {
    try {
      if (!this.isInitialized()) {
        if (this.config.debugMode) {
          console.warn('Analytics: Service not initialized, queuing event', event);
        }
        this.queueEvent(event);
        return;
      }

      const enrichedEvent = this.enrichEvent(event);
      
      if (this.config.batchEvents) {
        this.queueEvent(enrichedEvent);
        this.scheduleBatch();
      } else {
        this.sendEvent(enrichedEvent);
      }

      this.updateSession();

    } catch (error) {
      console.error('Analytics: Failed to track event', error);
      // Don't throw - analytics should never break the application
    }
  }

  public trackGameEvent(event: GameStartEvent | GameCompleteEvent): void {
    this.trackEvent(event);
  }

  public trackUserBehavior(event: UserEngagementEvent | SettingsChangeEvent | ModeSwitchEvent | ShareEvent | ModalOpenEvent): void {
    this.trackEvent(event);
  }

  public trackPerformance(event: PerformanceIssueEvent): void {
    this.trackEvent(event);
  }

  public trackConversion(event: FirstGameCompleteEvent | StreakMilestoneEvent): void {
    this.trackEvent(event);
  }

  public setUserProperty(property: string, value: string | number | boolean): void {
    try {
      if (!this.isInitialized()) return;

      this.userProperties = {
        ...this.userProperties,
        [property]: value
      };

      if (window.gtag) {
        window.gtag('set', 'user_properties', {
          [property]: value
        });
      }

    } catch (error) {
      console.error('Analytics: Failed to set user property', error);
    }
  }

  public async flush(): Promise<void> {
    if (this.eventQueue.size === 0) return;

    try {
      const events = [...this.eventQueue.events];
      this.eventQueue.events = [];
      this.eventQueue.size = 0;
      this.eventQueue.lastFlush = Date.now();

      for (const event of events) {
        this.sendEvent(event);
      }

      if (this.config.debugMode) {
        console.log(`Analytics: Flushed ${events.length} events`);
      }

    } catch (error) {
      console.error('Analytics: Failed to flush events', error);
    }
  }

  // Private methods

  private async loadGoogleAnalytics(): Promise<void> {
    return new Promise((resolve, reject) => {
      try {
        // Create and load gtag script
        const script = document.createElement('script');
        script.async = true;
        script.src = `https://www.googletagmanager.com/gtag/js?id=${this.config.measurementId}`;
        
        script.onload = () => {
          if (this.config.debugMode) {
            console.log('Analytics: GA4 script loaded, configuring...');
          }

          // gtag function is now globally available from index.html
          // Configure GA4
          window.gtag('js', new Date());
          window.gtag('config', this.config.measurementId, {
            // Privacy settings
            anonymize_ip: true,
            allow_google_signals: false,
            // Performance settings
            send_page_view: false, // We'll send manually
            // Enable debug mode for GA4 DebugView
            debug_mode: this.config.debugMode,
            // Custom parameter mapping
            custom_map: {
              'custom_parameter_game_mode': 'game_mode',
              'custom_parameter_word_length': 'word_length',
              'custom_parameter_score': 'score'
            }
          });

          if (this.config.debugMode) {
            console.log('Analytics: GA4 configured for measurement ID:', this.config.measurementId);
          }

          resolve();
        };

        script.onerror = () => {
          reject(new Error('Failed to load Google Analytics script'));
        };

        document.head.appendChild(script);

      } catch (error) {
        reject(error);
      }
    });
  }

  private createSession(): AnalyticsSession {
    const now = Date.now();
    return {
      id: this.generateSessionId(),
      startTime: now,
      lastActivity: now,
      eventCount: 0,
      gameCount: 0
    };
  }

  private generateSessionId(): string {
    return `${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;
  }

  private initializeUserProperties(): UserProperties {
    try {
      const sessionCount = this.getSessionCount();
      const totalGames = this.getTotalGamesPlayed();
      
      return {
        user_type: sessionCount === 1 ? 'new' : 'returning',
        session_count: sessionCount,
        total_games_played: totalGames
      };
    } catch (error) {
      return {
        user_type: 'new',
        session_count: 1,
        total_games_played: 0
      };
    }
  }

  private getSessionCount(): number {
    try {
      const stored = localStorage.getItem('analytics_session_count');
      const count = stored ? parseInt(stored, 10) + 1 : 1;
      localStorage.setItem('analytics_session_count', count.toString());
      return count;
    } catch {
      return 1;
    }
  }

  private getTotalGamesPlayed(): number {
    try {
      const stored = localStorage.getItem('analytics_total_games');
      return stored ? parseInt(stored, 10) : 0;
    } catch {
      return 0;
    }
  }

  private initializeSessionTracking(): void {
    // Track when user becomes inactive/active
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        this.updateSession();
      }
    });

    // Track session end on beforeunload
    window.addEventListener('beforeunload', () => {
      this.flush();
    });
  }

  private setupPerformanceMonitoring(): void {
    // Integration with existing Web Vitals will be added in next task
    if (this.config.debugMode) {
      console.log('Analytics: Performance monitoring setup ready');
    }
  }

  private enrichEvent(event: AnalyticsEvent): AnalyticsEvent {
    return {
      ...event,
      timestamp: Date.now(),
      session_id: this.session.id
    };
  }

  private queueEvent(event: AnalyticsEvent): void {
    this.eventQueue.events.push(event);
    this.eventQueue.size = this.eventQueue.events.length;
  }

  private scheduleBatch(): void {
    const shouldFlushSize = this.eventQueue.size >= this.config.batchSize;
    const shouldFlushTime = Date.now() - this.eventQueue.lastFlush >= this.config.batchTimeout;

    if (shouldFlushSize || shouldFlushTime) {
      this.flush();
      return;
    }

    // Schedule timeout if not already scheduled
    if (!this.flushTimeout) {
      this.flushTimeout = setTimeout(() => {
        this.flushTimeout = null;
        this.flush();
      }, this.config.batchTimeout);
    }
  }

  private sendEvent(event: AnalyticsEvent): void {
    if (!window.gtag) {
      if (this.config.debugMode) {
        console.warn('Analytics: gtag not available, event not sent', event);
      }
      return;
    }

    try {
      const { event_name, timestamp, session_id, ...eventData } = event;
      
      window.gtag('event', event_name, {
        ...eventData,
        event_category: this.getEventCategory(event_name),
        event_label: session_id,
        value: this.getEventValue(event),
        // Add user properties
        user_type: this.userProperties.user_type,
        session_count: this.userProperties.session_count
      });

      if (this.config.debugMode) {
        console.log('Analytics: Event sent', { event_name, ...eventData });
      }

    } catch (error) {
      console.error('Analytics: Failed to send event', error);
    }
  }

  private getEventCategory(eventName: string): string {
    if (eventName.includes('game')) return 'game_interaction';
    if (eventName.includes('daily_challenge')) return 'daily_challenge';
    if (eventName.includes('settings') || eventName.includes('mode_switch')) return 'user_preferences';
    if (eventName.includes('performance') || eventName.includes('error')) return 'technical';
    if (eventName.includes('engagement') || eventName.includes('visit')) return 'user_behavior';
    return 'general';
  }

  private getEventValue(event: AnalyticsEvent): number | undefined {
    if ('score' in event) return event.score as number;
    if ('time_duration' in event) return event.time_duration as number;
    if ('streak_length' in event) return event.streak_length as number;
    if ('value' in event) return event.value as number;
    return undefined;
  }

  private updateSession(): void {
    this.session.lastActivity = Date.now();
    this.session.eventCount++;
  }

  private trackPageView(): void {
    this.trackEvent({
      event_name: 'page_view',
      timestamp: Date.now(),
      session_id: this.session.id,
      user_session_count: this.userProperties.session_count,
      time_since_last_visit: this.getTimeSinceLastVisit()
    });
  }

  private getTimeSinceLastVisit(): number | undefined {
    try {
      const lastVisit = localStorage.getItem('analytics_last_visit');
      const now = Date.now();
      
      if (lastVisit) {
        const timeSince = now - parseInt(lastVisit, 10);
        localStorage.setItem('analytics_last_visit', now.toString());
        return timeSince;
      } else {
        localStorage.setItem('analytics_last_visit', now.toString());
        return undefined;
      }
    } catch {
      return undefined;
    }
  }
}